var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import CreateApiClient from "./CreateApiClient";
import { productProjectionNormalization, productDataNormalization, productSearchNormalization, } from "@/utils/dataNormalization";
import { getMockCategories, getMockProductByKey, getMockProducts, searchMockProductsByCategory, searchMockProductsByName, } from "@/api/mock/mockData";
import { ClientBuilder, } from "@commercetools/ts-client";
const HAS_CTP_CONFIG = Boolean(process.env.REACT_APP_BASE_URL
    && process.env.REACT_APP_OAUTH_URL
    && process.env.REACT_APP_PROJECT_KEY
    && process.env.REACT_APP_ADMIN_CLIENT_ID
    && process.env.REACT_APP_ADMIN_CLIENT_SECRET
    && process.env.REACT_APP_SPA_CLIENT_ID
    && process.env.REACT_APP_SPA_CLIENT_SECRET);
const MOCK_MODE = process.env.REACT_APP_USE_MOCK_DATA === "true" || !HAS_CTP_CONFIG;
const MOCK_AUTH_API_URL = process.env.REACT_APP_MOCK_AUTH_API_URL || "";
const MOCK_USERS_STORAGE_KEY = "mockUsers";
const MOCK_CURRENT_CUSTOMER_KEY = "mockCurrentCustomer";
export class ApiClient extends CreateApiClient {
    constructor() {
        super();
        if (!MOCK_MODE) {
            this.getAllCarts();
        }
    }
    isMockMode() {
        return MOCK_MODE;
    }
    isMockDbAuthEnabled() {
        return MOCK_MODE && Boolean(MOCK_AUTH_API_URL);
    }
    requestMockAuth(path_1) {
        return __awaiter(this, arguments, void 0, function* (path, options = {}) {
            const headers = Object.assign({ "Content-Type": "application/json" }, options.headers);
            const response = yield fetch(`${MOCK_AUTH_API_URL}${path}`, Object.assign(Object.assign({}, options), { headers }));
            const data = yield response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.message || "Mock auth request failed");
            }
            return data;
        });
    }
    saveMockSessionFromToken(token, expirationTime) {
        const cache = { token, expirationTime };
        localStorage.setItem("accessToken", JSON.stringify(cache));
    }
    saveCartToDb() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isMockDbAuthEnabled()) {
                return;
            }
            try {
                const token = localStorage.getItem("accessToken");
                if (!token) {
                    console.error("[saveCartToDb] No token found");
                    return;
                }
                const tokenData = JSON.parse(token);
                if (!tokenData.token) {
                    console.error("[saveCartToDb] No token in parsed data");
                    return;
                }
                const cartData = localStorage.getItem("mockCart");
                if (!cartData) {
                    console.error("[saveCartToDb] No cart data in localStorage");
                    return;
                }
                const cart = JSON.parse(cartData);
                const response = yield this.requestMockAuth("/auth/save-cart", {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${tokenData.token}`,
                    },
                    body: JSON.stringify({ cart }),
                });
            }
            catch (error) {
                console.error("[saveCartToDb] Failed to save cart to database:", error);
            }
        });
    }
    loadCartFromDb() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isMockDbAuthEnabled()) {
                console.error("[loadCartFromDb] Mock DB auth not enabled");
                return;
            }
            try {
                const token = localStorage.getItem("accessToken");
                if (!token) {
                    console.error("[loadCartFromDb] No token found");
                    return;
                }
                const tokenData = JSON.parse(token);
                if (!tokenData.token) {
                    console.error("[loadCartFromDb] No token in parsed data");
                    return;
                }
                const result = yield this.requestMockAuth("/auth/get-cart", {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${tokenData.token}`,
                    },
                });
                if (result.cart) {
                    localStorage.setItem("mockCart", JSON.stringify(result.cart));
                }
                else {
                    console.error("[loadCartFromDb] No cart in response");
                }
            }
            catch (error) {
                console.error("[loadCartFromDb] Failed to load cart from database:", error);
            }
        });
    }
    readMockUsers() {
        const raw = localStorage.getItem(MOCK_USERS_STORAGE_KEY);
        if (!raw) {
            const seededUsers = [
                {
                    id: "mock-customer-demo",
                    version: 1,
                    email: "demo@shop.local",
                    firstName: "Demo",
                    lastName: "User",
                    dateOfBirth: "1995-01-01",
                    addresses: [
                        {
                            id: "addr-demo-1",
                            streetName: "Mock Street 1",
                            postalCode: "00100",
                            city: "Helsinki",
                            country: "FI",
                        },
                    ],
                    defaultShippingAddressId: "addr-demo-1",
                    defaultBillingAddressId: "addr-demo-1",
                    password: "Demo123!",
                },
            ];
            this.writeMockUsers(seededUsers);
            return seededUsers;
        }
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        }
        catch (_a) {
            return [];
        }
    }
    writeMockUsers(users) {
        localStorage.setItem(MOCK_USERS_STORAGE_KEY, JSON.stringify(users));
    }
    toCustomer(record) {
        return {
            id: record.id,
            version: record.version,
            email: record.email,
            firstName: record.firstName,
            lastName: record.lastName,
            dateOfBirth: record.dateOfBirth,
            addresses: record.addresses || [],
            defaultShippingAddressId: record.defaultShippingAddressId,
            defaultBillingAddressId: record.defaultBillingAddressId,
        };
    }
    saveMockSession(email) {
        const token = {
            token: `mock-token-${email}`,
            expirationTime: Date.now() + 24 * 60 * 60 * 1000,
        };
        localStorage.setItem("accessToken", JSON.stringify(token));
        localStorage.setItem(MOCK_CURRENT_CUSTOMER_KEY, email);
    }
    getMockCurrentCustomerRecord() {
        const currentEmail = localStorage.getItem(MOCK_CURRENT_CUSTOMER_KEY);
        if (!currentEmail)
            return null;
        const users = this.readMockUsers();
        return users.find((user) => user.email === currentEmail) || null;
    }
    updateMockCustomerRecord(updated) {
        const users = this.readMockUsers();
        const nextUsers = users.map((user) => user.email === updated.email || user.id === updated.id ? updated : user);
        this.writeMockUsers(nextUsers);
        localStorage.setItem(MOCK_CURRENT_CUSTOMER_KEY, updated.email);
    }
    /**
     * BUILD CUSTOMER WITH PASSWORD
     */
    getCustomerWithPassword(email, password) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (MOCK_MODE) {
                if (this.isMockDbAuthEnabled()) {
                    const result = yield this.requestMockAuth("/auth/login", {
                        method: "POST",
                        body: JSON.stringify({ email, password }),
                    });
                    this.saveMockSessionFromToken(result.token, result.expirationTime);
                    localStorage.setItem(MOCK_CURRENT_CUSTOMER_KEY, result.customer.email);
                    // Load cart from database after successful login
                    yield this.loadCartFromDb();
                    return result.customer;
                }
                const users = this.readMockUsers();
                const matched = users.find((user) => user.email === email && user.password === password);
                if (!matched) {
                    throw new Error("Invalid email or password");
                }
                this.saveMockSession(matched.email);
                return this.toCustomer(matched);
            }
            try {
                this.client = this.buildClientWithPassword(email, password);
                const apiRoot = this.getApiRootSafe();
                const response = yield apiRoot
                    .withProjectKey({ projectKey: this.PROJECT_KEY })
                    .me()
                    .get()
                    .execute();
                return response.body;
            }
            catch (error) {
                console.error("Failed to get customer with password:", error);
                // Проверка: это стандартная ошибка с текстом
                if (error instanceof Error &&
                    error.message.includes("Customer account with the given credentials not found")) {
                    throw new Error("Invalid email or password");
                }
                // Проверка: это объект с полем 'body.message'
                if (typeof error === "object" &&
                    error !== null &&
                    "body" in error &&
                    typeof error.body === "object" &&
                    ((_a = error.body) === null || _a === void 0 ? void 0 : _a.message) &&
                    typeof error.body.message ===
                        "string") {
                    throw new Error(error.body.message);
                }
                throw new Error("Authentication failed");
            }
        });
    }
    /**
     * BUILD CUSTOMER WITH TOKEN
     */
    getCustomerWithToken(token) {
        return __awaiter(this, void 0, void 0, function* () {
            if (MOCK_MODE) {
                if (this.isMockDbAuthEnabled()) {
                    const result = yield this.requestMockAuth("/auth/me", {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    localStorage.setItem(MOCK_CURRENT_CUSTOMER_KEY, result.customer.email);
                    return result.customer;
                }
                if (!(token === null || token === void 0 ? void 0 : token.startsWith("mock-token-"))) {
                    throw new Error("Failed to fetch customer by token");
                }
                const email = token.replace("mock-token-", "");
                const users = this.readMockUsers();
                const matched = users.find((user) => user.email === email);
                if (!matched) {
                    throw new Error("Failed to fetch customer by token");
                }
                this.saveMockSession(matched.email);
                return this.toCustomer(matched);
            }
            this.client = this.buildClientWithToken(token);
            const apiRoot = this.getApiRootSafe();
            try {
                const { body: customer } = yield apiRoot
                    .withProjectKey({ projectKey: this.PROJECT_KEY })
                    .me()
                    .get()
                    .execute();
                return customer;
            }
            catch (error) {
                console.error(error);
                throw new Error("Failed to fetch customer by token");
            }
        });
    }
    /**
     * SIGN IN CUSTOMER
     */
    loginCustomer(customerData) {
        return __awaiter(this, void 0, void 0, function* () {
            if (MOCK_MODE) {
                const email = customerData.email;
                const password = customerData.password || "mock-password";
                return this.getCustomerWithPassword(email, password);
            }
            const apiRoot = this.getApiRoot(this.defaultClient);
            try {
                const { body: customer } = yield apiRoot
                    .withProjectKey({
                    projectKey: this.PROJECT_KEY,
                })
                    .me()
                    .login()
                    .post({ body: customerData })
                    .execute();
                return customer;
            }
            catch (error) {
                console.error("Update failed", error);
                throw new Error("Failed to update customer");
            }
        });
    }
    /**
     * REGISTER CUSTOMER
     */
    registerCustomer(customerData) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            if (MOCK_MODE) {
                if (this.isMockDbAuthEnabled()) {
                    const result = yield this.requestMockAuth("/auth/register", {
                        method: "POST",
                        body: JSON.stringify(customerData),
                    });
                    this.saveMockSessionFromToken(result.token, result.expirationTime);
                    localStorage.setItem(MOCK_CURRENT_CUSTOMER_KEY, result.customer.email);
                    // Load cart from database after successful registration
                    yield this.loadCartFromDb();
                    return {
                        customer: result.customer,
                    };
                }
                const users = this.readMockUsers();
                const exists = users.some((user) => user.email === customerData.email);
                if (exists) {
                    throw new Error("A customer with this email already exists.");
                }
                const addresses = (customerData.addresses || []).map((address, index) => ({
                    id: address.id || `addr-${Date.now()}-${index}`,
                    streetName: address.streetName,
                    postalCode: address.postalCode,
                    city: address.city,
                    state: address.state,
                    country: address.country,
                }));
                const newUser = {
                    id: `mock-customer-${Date.now()}`,
                    version: 1,
                    email: customerData.email,
                    firstName: customerData.firstName || "Demo",
                    lastName: customerData.lastName || "User",
                    dateOfBirth: customerData.dateOfBirth,
                    addresses,
                    defaultShippingAddressId: (_a = addresses[customerData.defaultShippingAddress || 0]) === null || _a === void 0 ? void 0 : _a.id,
                    defaultBillingAddressId: (_b = addresses[customerData.defaultBillingAddress || 0]) === null || _b === void 0 ? void 0 : _b.id,
                    password: customerData.password || "mock-password",
                };
                this.writeMockUsers([...users, newUser]);
                this.saveMockSession(newUser.email);
                return {
                    customer: this.toCustomer(newUser),
                };
            }
            const client = this.buildDefaultClient(false);
            this.apiRoot = this.getApiRoot(client);
            try {
                const { body: customer } = yield this.apiRoot
                    .withProjectKey({
                    projectKey: this.PROJECT_KEY,
                })
                    .me()
                    .signup()
                    .post({ body: customerData })
                    .execute();
                return customer;
            }
            catch (error) {
                const err = error;
                const duplicateEmail = (_c = err.body.errors) === null || _c === void 0 ? void 0 : _c.find((e) => e.code === "DuplicateField" && e.field === "email");
                if (duplicateEmail) {
                    throw new Error("A customer with this email already exists.");
                }
                throw new Error(err.body.message || "Registration failed. Try again.");
            }
        });
    }
    /**
     * GET CUSTOMER PROFILE
     */
    getCustomerProfile() {
        return __awaiter(this, void 0, void 0, function* () {
            if (MOCK_MODE) {
                if (this.isMockDbAuthEnabled()) {
                    const raw = localStorage.getItem("accessToken");
                    const stored = raw ? JSON.parse(raw) : null;
                    const token = stored === null || stored === void 0 ? void 0 : stored.token;
                    if (!token) {
                        throw new Error("Unauthorized action");
                    }
                    const result = yield this.requestMockAuth("/auth/me", {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    localStorage.setItem(MOCK_CURRENT_CUSTOMER_KEY, result.customer.email);
                    return result.customer;
                }
                const customer = this.getMockCurrentCustomerRecord();
                if (!customer) {
                    throw new Error("Unauthorized action");
                }
                return this.toCustomer(customer);
            }
            const apiRoot = this.getApiRoot(this.client);
            if (!apiRoot)
                throw new Error("Unauthorized action");
            try {
                const { body: customer } = yield apiRoot
                    .withProjectKey({
                    projectKey: this.PROJECT_KEY,
                })
                    .me()
                    .get()
                    .execute();
                return customer;
            }
            catch (error) {
                console.error(error);
                throw new Error("Failed to fetch customer profile");
            }
        });
    }
    /**
     * GET CATEGORIES
     */
    getAllCategories(args) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (MOCK_MODE) {
                return getMockCategories(args);
            }
            const apiRoot = this.getApiRoot(this.defaultClient);
            try {
                const { body: data } = yield apiRoot
                    .withProjectKey({
                    projectKey: this.PROJECT_KEY,
                })
                    .categories()
                    .get({ queryArgs: args })
                    .execute();
                return data;
            }
            catch (error) {
                console.error(error);
                return {
                    limit: (_a = args === null || args === void 0 ? void 0 : args.limit) !== null && _a !== void 0 ? _a : 0,
                    offset: 0,
                    count: 0,
                    total: 0,
                    results: [],
                };
            }
        });
    }
    /**
     * GET ALL PRODUCTS
     */
    getAllProducts(args) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (MOCK_MODE) {
                return getMockProducts(args);
            }
            const apiRoot = this.getApiRoot(this.defaultClient);
            try {
                const { body: data } = yield apiRoot
                    .withProjectKey({ projectKey: this.PROJECT_KEY })
                    .productProjections()
                    .get({ queryArgs: args })
                    .execute();
                const normalized = productProjectionNormalization(data);
                return { products: normalized, total: (_a = data.total) !== null && _a !== void 0 ? _a : normalized.length };
            }
            catch (error) {
                console.error(error);
                return { products: [], total: 0 };
            }
        });
    }
    /**
     * GET PRODUCT WITH KEY
     */
    getProduct(key) {
        return __awaiter(this, void 0, void 0, function* () {
            if (MOCK_MODE) {
                const product = getMockProductByKey(key);
                if (!product) {
                    throw new Error("Product not found");
                }
                return product;
            }
            const apiRoot = this.getApiRoot(this.defaultClient);
            try {
                const { body: data } = yield apiRoot
                    .withProjectKey({
                    projectKey: this.PROJECT_KEY,
                })
                    .products()
                    .withKey({ key: key })
                    .get()
                    .execute();
                return productDataNormalization(data);
            }
            catch (error) {
                console.error(error);
                throw new Error("Product not found");
            }
        });
    }
    /**
     * SEARCH DATA
     */
    // public async searchData(
    //   searchType: SearchTypes,
    //   searchValue: string,
    //   options: {
    //     limit?: number;
    //     offset?: number;
    //     sort?: string | string[];
    //     minPrice?: number;
    //     maxPrice?: number;
    //     discountOnly?: boolean;
    //   } = {}
    // ): Promise<{ products: MyProductsData[]; total: number }> {
    //   const apiRoot = this.getApiRoot(this.defaultClient);
    //   const filterArgs: string[] = [];
    //   if (typeof options.minPrice === "number") {
    //     filterArgs.push(
    //       `variants.price.centAmount:range(${options.minPrice * 100} to *)`
    //     );
    //   }
    //   if (typeof options.maxPrice === "number") {
    //     filterArgs.push(
    //       `variants.price.centAmount:range(* to ${options.maxPrice * 100})`
    //     );
    //   }
    //   if (options.discountOnly) {
    //     filterArgs.push("variants.prices.discounted.exists:true");
    //   }
    //   const queryArgs: {
    //     [key: string]: string | string[] | number | boolean | undefined;
    //   } = {
    //     limit: options.limit,
    //     offset: options.offset,
    //     sort: options.sort,
    //   };
    //   if (searchType === "name") {
    //     queryArgs["text.en-US"] = searchValue;
    //   } else if (searchType === "category") {
    //     filterArgs.push(`categories.id:"${searchValue}"`);
    //   }
    //   if (filterArgs.length > 0) {
    //     queryArgs["filter.query"] = filterArgs;
    //   }
    //   try {
    //     const { body } = await apiRoot
    //       .withProjectKey({ projectKey: this.PROJECT_KEY })
    //       .productProjections()
    //       .search()
    //       .get({ queryArgs })
    //       .execute();
    //     const products = productProjectionNormalization({ results: body.results });
    //     const total = body.total ?? products.length;
    //     return { products, total };
    //   } catch (error) {
    //     console.error("Failed to search products:", error);
    //     throw new Error("Failed to fetch filtered products");
    //   }
    // }
    // ***** SEARCH DATA *****
    searchData(searchType, searchValue) {
        return __awaiter(this, void 0, void 0, function* () {
            if (MOCK_MODE) {
                if (searchType === "name") {
                    return searchMockProductsByName(searchValue);
                }
                return searchMockProductsByCategory(searchValue);
            }
            const apiRoot = this.getApiRoot(this.defaultClient);
            let searchArgs = {};
            switch (searchType) {
                case "name":
                    searchArgs = { "text.en-US": searchValue, limit: 10 };
                    break;
                case "category":
                    searchArgs = { "filter.query": `categories.id:"${searchValue}"` };
                    break;
            }
            try {
                const { body: data } = yield apiRoot
                    .withProjectKey({
                    projectKey: this.PROJECT_KEY,
                })
                    .productProjections()
                    .search()
                    .get({
                    queryArgs: searchArgs,
                })
                    .execute();
                return productSearchNormalization(data);
            }
            catch (error) {
                console.error(error);
                return [];
            }
        });
    }
    /**
     * UPDATE CUSTOMER
     */
    updateCustomer(updatePayload) {
        return __awaiter(this, void 0, void 0, function* () {
            if (MOCK_MODE) {
                if (this.isMockDbAuthEnabled()) {
                    const raw = localStorage.getItem("accessToken");
                    const stored = raw ? JSON.parse(raw) : null;
                    const token = stored === null || stored === void 0 ? void 0 : stored.token;
                    if (!token) {
                        throw new Error("Unauthorized action");
                    }
                    const result = yield this.requestMockAuth("/auth/update", {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(updatePayload),
                    });
                    localStorage.setItem(MOCK_CURRENT_CUSTOMER_KEY, result.customer.email);
                    return result.customer;
                }
                const current = this.getMockCurrentCustomerRecord();
                if (!current)
                    throw new Error("Unauthorized action");
                const actions = (updatePayload.actions || []);
                const next = Object.assign(Object.assign({}, current), { version: current.version + 1, addresses: [...(current.addresses || [])] });
                for (const action of actions) {
                    if (action.action === "setFirstName" && action.firstName) {
                        next.firstName = action.firstName;
                    }
                    if (action.action === "setLastName" && action.lastName) {
                        next.lastName = action.lastName;
                    }
                    if (action.action === "setDateOfBirth" && action.dateOfBirth) {
                        next.dateOfBirth = action.dateOfBirth;
                    }
                    if (action.action === "changeEmail" && action.email) {
                        next.email = action.email;
                    }
                    if (action.action === "changeAddress" && action.addressId && action.address) {
                        next.addresses = (next.addresses || []).map((address) => address.id === action.addressId ? Object.assign(Object.assign({}, address), action.address) : address);
                    }
                    if (action.action === "addAddress" && action.address) {
                        next.addresses = [
                            ...(next.addresses || []),
                            Object.assign(Object.assign({}, action.address), { id: action.address.id || `addr-${Date.now()}` }),
                        ];
                    }
                    if (action.action === "setDefaultShippingAddress") {
                        next.defaultShippingAddressId = action.addressId;
                    }
                    if (action.action === "setDefaultBillingAddress") {
                        next.defaultBillingAddressId = action.addressId;
                    }
                }
                this.updateMockCustomerRecord(next);
                return this.toCustomer(next);
            }
            const apiRoot = this.getApiRoot(this.client);
            if (!apiRoot)
                throw new Error("Unauthorized action");
            try {
                const { body: data } = yield apiRoot
                    .withProjectKey({ projectKey: this.PROJECT_KEY })
                    .me()
                    .post({ body: updatePayload })
                    .execute();
                return data;
            }
            catch (error) {
                console.error(error);
                throw new Error("Failed to update customer");
            }
        });
    }
    changePassword(currentPassword, newPassword, version) {
        return __awaiter(this, void 0, void 0, function* () {
            if (MOCK_MODE) {
                if (this.isMockDbAuthEnabled()) {
                    const raw = localStorage.getItem("accessToken");
                    const stored = raw ? JSON.parse(raw) : null;
                    const token = stored === null || stored === void 0 ? void 0 : stored.token;
                    if (!token) {
                        throw new Error("Unauthorized action");
                    }
                    yield this.requestMockAuth("/auth/change-password", {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ currentPassword, newPassword, version }),
                    });
                    return;
                }
                const current = this.getMockCurrentCustomerRecord();
                if (!current)
                    throw new Error("Unauthorized action");
                if (current.password !== currentPassword) {
                    throw new Error("InvalidCurrentPassword");
                }
                const updated = Object.assign(Object.assign({}, current), { version: Math.max(version + 1, current.version + 1), password: newPassword });
                this.updateMockCustomerRecord(updated);
                return;
            }
            const apiRoot = this.getApiRoot(this.client);
            yield apiRoot
                .withProjectKey({ projectKey: this.PROJECT_KEY })
                .me()
                .password()
                .post({
                body: {
                    version,
                    currentPassword,
                    newPassword,
                },
            })
                .execute();
        });
    }
    getMyActiveCart() {
        return __awaiter(this, void 0, void 0, function* () {
            const apiRoot = this.getApiRoot(this.client);
            if (!apiRoot)
                throw new Error("Unauthorized action");
            try {
                const { body: cart } = yield apiRoot
                    .withProjectKey({ projectKey: this.PROJECT_KEY })
                    .me()
                    .activeCart()
                    .get()
                    .execute();
                return cart;
            }
            catch (error) {
                console.error(error);
                throw new Error("Failed to fetch active cart");
            }
        });
    }
    getAllCarts() {
        return __awaiter(this, void 0, void 0, function* () {
            const apiRoot = this.getApiRoot(this.defaultClient);
            if (!apiRoot)
                throw new Error("Unauthorized action");
            try {
                const { body: cart } = yield apiRoot
                    .withProjectKey({ projectKey: this.PROJECT_KEY })
                    .carts()
                    .get()
                    .execute();
                return cart;
            }
            catch (error) {
                console.error(error);
                throw new Error("Failed to fetch active cart");
            }
        });
    }
    createMyCart(customer) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const apiRoot = this.getApiRoot(this.client);
            if (!apiRoot)
                throw new Error("Unauthorized action");
            const shippingAddress = (_a = customer === null || customer === void 0 ? void 0 : customer.addresses) === null || _a === void 0 ? void 0 : _a.find((addr) => addr.id === customer.defaultShippingAddressId);
            const countryFromCustomer = shippingAddress === null || shippingAddress === void 0 ? void 0 : shippingAddress.country;
            const body = {
                currency: "EUR",
            };
            if (!(customer === null || customer === void 0 ? void 0 : customer.id)) {
                body.anonymousId = this.getOrCreateAnonymousId();
            }
            if (countryFromCustomer) {
                body.country = countryFromCustomer;
            }
            const { body: cart } = yield apiRoot
                .withProjectKey({ projectKey: this.PROJECT_KEY })
                .me()
                .carts()
                .post({ body })
                .execute();
            return cart;
        });
    }
    addProductToCart(productId_1) {
        return __awaiter(this, arguments, void 0, function* (productId, variantId = 1, customer) {
            const apiRoot = this.getApiRoot(this.client);
            if (!apiRoot)
                throw new Error("Unauthorized action");
            try {
                let cart;
                try {
                    cart = yield this.getMyActiveCart();
                }
                catch (_a) {
                    cart = yield this.createMyCart(customer);
                }
                const payload = {
                    version: cart.version,
                    actions: [
                        {
                            action: "addLineItem",
                            productId,
                            variantId,
                            quantity: 1,
                        },
                    ],
                };
                const updatedCart = yield apiRoot
                    .withProjectKey({ projectKey: this.PROJECT_KEY })
                    .me()
                    .carts()
                    .withId({ ID: cart.id })
                    .post({ body: payload })
                    .execute();
                return updatedCart.body;
            }
            catch (error) {
                console.error("Failed to add product to cart:", error);
                throw new Error("Add to cart failed");
            }
        });
    }
    getCartById(cartId) {
        return __awaiter(this, void 0, void 0, function* () {
            const apiRoot = this.getApiRoot(this.client);
            const { body: cart } = yield apiRoot
                .withProjectKey({ projectKey: this.PROJECT_KEY })
                .me()
                .carts()
                .withId({ ID: cartId })
                .get()
                .execute();
            return cart;
        });
    }
    initAnonymousClient() {
        if (MOCK_MODE) {
            return;
        }
        const anonymousId = this.getOrCreateAnonymousId();
        const options = {
            host: this.OAUTH_URI,
            projectKey: this.PROJECT_KEY,
            credentials: this.SPA_CREDENTIALS,
            scopes: [
                `manage_my_profile:${this.PROJECT_KEY}`,
                `manage_my_orders:${this.PROJECT_KEY}`,
                `view_published_products:${this.PROJECT_KEY}`,
            ],
            anonymousId,
            tokenCache: {
                get: () => {
                    const cached = localStorage.getItem("accessToken");
                    return cached
                        ? JSON.parse(cached)
                        : { token: "", expirationTime: 0 };
                },
                set: (cache) => {
                    localStorage.setItem("accessToken", JSON.stringify(cache));
                },
            },
            fetch,
        };
        this.client = new ClientBuilder()
            .withAnonymousSessionFlow(options)
            .withHttpMiddleware({ host: this.BASE_URI })
            .build();
    }
    /**
     * Initialize the client from localStorage
     * If no valid token is found, fallback to anonymous client
     */
    initClientFromStorage() {
        if (MOCK_MODE) {
            return;
        }
        const raw = localStorage.getItem("accessToken");
        if (raw) {
            try {
                const token = JSON.parse(raw);
                const now = Date.now();
                // const hasManageOrders = token.token?.includes("manage_orders");
                if (token.expirationTime && token.expirationTime > now) {
                    this.client = this.buildClientWithToken(token.token);
                    return;
                }
                console.warn("Token invalid or insufficient scope — removing.");
                localStorage.removeItem("accessToken");
            }
            catch (e) {
                console.error("Failed to parse accessToken", e);
                localStorage.removeItem("accessToken");
            }
        }
        // Fallback to anonymous
        this.initAnonymousClient();
    }
    /**
     * Remove a line item from the cart
     */
    removeLineItemFromCart(cartId, version, lineItemId) {
        return __awaiter(this, void 0, void 0, function* () {
            const apiRoot = this.getApiRoot(this.client);
            if (!apiRoot)
                throw new Error("Unauthorized action");
            const body = {
                version,
                actions: [
                    {
                        action: "removeLineItem",
                        lineItemId,
                    },
                ],
            };
            const res = yield apiRoot
                .withProjectKey({ projectKey: this.PROJECT_KEY })
                .me()
                .carts()
                .withId({ ID: cartId })
                .post({ body })
                .execute();
            return res.body;
        });
    }
    clearMyCart(cartId, version, lineItemIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const apiRoot = this.getApiRoot(this.client);
            const body = {
                version,
                actions: lineItemIds.map((lineItemId) => ({
                    action: "removeLineItem",
                    lineItemId,
                })),
            };
            const res = yield apiRoot
                .withProjectKey({ projectKey: this.PROJECT_KEY })
                .me()
                .carts()
                .withId({ ID: cartId })
                .post({ body })
                .execute();
            return res.body;
        });
    }
    deleteCart(cartId, version) {
        return __awaiter(this, void 0, void 0, function* () {
            const apiRoot = this.getApiRoot(this.client);
            if (!apiRoot)
                throw new Error("Unauthorized action");
            yield apiRoot
                .withProjectKey({ projectKey: this.PROJECT_KEY })
                .me()
                .carts()
                .withId({ ID: cartId })
                .delete({ queryArgs: { version } })
                .execute();
        });
    }
    get publicApiRoot() {
        return this.getApiRootSafe();
    }
    get publicProjectKey() {
        return this.PROJECT_KEY;
    }
    getApiRootSafe(client = this.client) {
        if (!client)
            throw new Error("API client is not initialized");
        return this.getApiRoot(client);
    }
    isAuthorized() {
        const raw = localStorage.getItem("accessToken");
        if (!raw)
            return false;
        try {
            const token = JSON.parse(raw);
            return token.expirationTime > Date.now();
        }
        catch (_a) {
            return false;
        }
    }
    logout() {
        return __awaiter(this, void 0, void 0, function* () {
            // Save cart to database before logging out
            if (MOCK_MODE && this.isMockDbAuthEnabled()) {
                yield this.saveCartToDb();
            }
            localStorage.removeItem("accessToken");
            localStorage.removeItem(MOCK_CURRENT_CUSTOMER_KEY);
            if (MOCK_MODE) {
                return;
            }
            this.initAnonymousClient();
        });
    }
}
// Singleton instance
export const apiClient = new ApiClient();
